import { Box, Drawer } from '@mui/material';
import type React from 'react';
import SpriteIcon from '../../../core-components/assets/Icon';
import ErrorScreen from '../../../core-components/theme/components/ErrorScreen';
import NestedAccordion, {
  type BaseAccordionItem,
  createAccordionItem,
  createAccordionSection,
} from '../../../core-components/theme/components/NestedAccordion';
import { useNotificationMetadata } from '../hooks/useNotificationMetadata';
import { FilterCategory } from '../hooks/useNotificationsFilter';
import { NotificationSidebarSkeleton } from './Skeletons/NotificationsSidebarSkeleton';
import { iconMap } from '../constants';
interface NotificationSidebarProps {
  selectedTypes: string[];
  selectedModules: string[];
  onSelect: (param: FilterCategory, value: string, isToggle: boolean) => void;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

export const NotificationSidebar: React.FC<NotificationSidebarProps> = ({
  selectedTypes,
  selectedModules,
  onSelect,
  mobileOpen = false,
  onMobileClose,
}) => {
  const { types, modules, isLoading, error } = useNotificationMetadata();

  const handleItemClick = (item: BaseAccordionItem, path: string[]) => {
    const sectionId = path[0] as FilterCategory;
    if (sectionId === 'type' || sectionId === 'module') {
      onSelect(sectionId, item.value, true);
    }
  };

  const typeItems = types.map(type =>
    createAccordionItem(type.id, type.label, {
      icon: iconMap[type.value],
      value: type.value
    })
  );

  const moduleItems = modules.map(module =>
    createAccordionItem(module.id, module.label, {
      icon: iconMap[module.value],
      value: module.value
    })
  );

  const sections = [
    createAccordionSection('type', 'Type', typeItems, {
      defaultExpanded: true,
    }),
    createAccordionSection('module', 'Module', moduleItems, {
      defaultExpanded: true,
    }),
  ];

  const sidebarContent = (
    <Box
      sx={{
        p: 2,
        height: '100%',
        overflow: 'auto',
      }}
    >
      {isLoading ? (
        <NotificationSidebarSkeleton />
      ) : error ? (
        <ErrorScreen icon={<SpriteIcon id='no_data_error' width="201" height="201"/>} message="Failed to load data" />
      ) : (
        <NestedAccordion
          sections={sections}
          selectedItems={[...selectedTypes, ...selectedModules]}
          onItemClick={handleItemClick}
          showBadges={false}
          showIcons={true}
          maxNestingLevel={1}
          keyToCompare="value"
        />
      )}
    </Box>
  );

  return (
    <>
      <Box
        sx={{
          width: 320,
          flexShrink: 0,
          p: 0,
          display: { xs: 'none', sm: 'block' },
        }}
      >
        {sidebarContent}
      </Box>

      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          width: 280,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: 280,
            boxSizing: 'border-box',
            mt: 8,
          },
        }}
      >
        {sidebarContent}
      </Drawer>
    </>
  );
};

export default NotificationSidebar;